import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import NavigationPrompt from 'react-router-navigation-prompt';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';

import { Button } from 'core/components/button';
import LeavePageDialog from 'core/components/leave-page-dialog';

import useStyles from './make-a-change-drawer.styles';

const DISCARD_TITLE = 'Discard changes';
const DISCARD_MESSAGE = "Your changes to this policy aren't saved.";

export const DRAWER_PHASES = { EDIT: 'edit', LOADING: 'loading', PREVIEW: 'preview' };

// Condensed layout: the side panel that holds the Details form while it's edited. It sits inside the Details
// Formik (context reaches through the portal), and its Preview button runs the same submit as the classic footer.
// The preview shows here first; "Review and save" opens today's Preview modal, which confirms the change.
const MakeAChangeDrawer = ({
  open,
  eyebrow,
  changed,
  phase,
  previewPanel,
  canPreview,
  onPreview,
  onEdit,
  onReviewAndSave,
  onClose,
  children
}) => {
  const classes = useStyles();
  const { dirty, resetForm } = useFormikContext();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const unsaved = dirty || changed;

  useEffect(() => {
    if (!open || !unsaved) {
      return undefined;
    }
    window.onbeforeunload = () => true;
    return () => {
      window.onbeforeunload = null;
    };
  }, [open, unsaved]);

  const requestClose = () => {
    if (unsaved) {
      setConfirmDiscard(true);
    } else {
      onClose();
    }
  };

  const discard = () => {
    setConfirmDiscard(false);
    resetForm();
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={requestClose}
      classes={{ paper: classes.paper }}
      PaperProps={{ role: 'dialog', 'aria-labelledby': 'make-a-change-title' }}
    >
      <NavigationPrompt renderIfNotActive={false} when={open && unsaved}>
        {({ isActive, onCancel, onConfirm }) => <LeavePageDialog open={isActive} onClose={onCancel} cb={onConfirm} />}
      </NavigationPrompt>
      <div className={classes.header}>
        <div>
          <span className={classes.eyebrow}>{eyebrow}</span>
          <h2 id="make-a-change-title" className={classes.title}>
            Make a change
          </h2>
        </div>
        <IconButton aria-label="Close" className={classes.close} onClick={requestClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.body}>
        {phase === DRAWER_PHASES.LOADING && (
          <p className={classes.wait} role="status">
            <CircularProgress size={16} />
            Calculating the price change
          </p>
        )}
        {phase === DRAWER_PHASES.PREVIEW && previewPanel}
        {children}
      </div>
      <div className={classes.footer}>
        {phase === DRAWER_PHASES.PREVIEW ? (
          <>
            <Button variant="outlined" color="primary" onClick={onEdit}>
              Edit
            </Button>
            <Button variant="contained" color="primary" onClick={onReviewAndSave}>
              Review and save
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" color="primary" onClick={requestClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onPreview}
              disabled={!canPreview || phase === DRAWER_PHASES.LOADING}
              data-cy="preview-policy-changes"
            >
              Preview price change
            </Button>
          </>
        )}
      </div>
      <LeavePageDialog
        open={confirmDiscard}
        title={DISCARD_TITLE}
        message={DISCARD_MESSAGE}
        onClose={() => setConfirmDiscard(false)}
        cb={discard}
      />
    </Drawer>
  );
};

MakeAChangeDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  eyebrow: PropTypes.string.isRequired,
  changed: PropTypes.bool,
  phase: PropTypes.oneOf(Object.values(DRAWER_PHASES)),
  previewPanel: PropTypes.node,
  canPreview: PropTypes.bool,
  onPreview: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onReviewAndSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
};

MakeAChangeDrawer.defaultProps = {
  changed: false,
  phase: DRAWER_PHASES.EDIT,
  previewPanel: null,
  canPreview: true
};

export default MakeAChangeDrawer;
